import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Plus, Pencil, Trash2, Library, Save, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function SubjectCatalogPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdult = user && !user.is_child;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id, code, name} eller {code:'',name:''} för ny
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/subjects");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Kunde inte hämta ämnen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ code: "", name: "", isNew: true });
  const startEdit = (item) => setEditing({ ...item, isNew: false });
  const cancelEdit = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    const code = (editing.code || "").trim().toUpperCase();
    const name = (editing.name || "").trim();
    if (!code || !name) {
      toast.error("Både kod och namn krävs");
      return;
    }
    if (!/^[A-ZÅÄÖ]{1,4}$/.test(code)) {
      toast.error("Kod måste vara 1-4 versaler");
      return;
    }
    setSaving(true);
    try {
      if (editing.isNew) {
        await api.post("/subjects", { code, name });
        toast.success(`${name} tillagt`);
      } else {
        await api.put(`/subjects/${editing.id}`, { code, name });
        toast.success(`${name} sparat`);
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Ta bort "${item.name}"? Detta påverkar inte befintliga betyg/event som redan använder ämnet.`)) return;
    try {
      await api.delete(`/subjects/${item.id}`);
      toast.success(`${item.name} togs bort`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte ta bort");
    }
  };

  return (
    <div className="px-5 pt-3 pb-12" data-testid="subject-catalog-page">
      <button
        onClick={() => nav(-1)}
        className="inline-flex items-center gap-1 text-sm text-[color:var(--text-secondary)] mb-3"
        data-testid="back-btn"
      >
        <ArrowLeft size={16} /> Tillbaka
      </button>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <p className="label-eyebrow">Inställningar</p>
          <h1 className="font-display text-2xl font-medium mt-0.5 flex items-center gap-2">
            <Library size={22} className="text-[color:var(--moss)]" /> Ämneskatalog
          </h1>
          <p className="text-xs text-[color:var(--text-secondary)] mt-1">
            Central lista över skolämnen — används i betyg, kalender, läxor och av Edit.
          </p>
        </div>
        {isAdult && !editing && (
          <button
            onClick={startNew}
            className="px-3 py-2 rounded-full bg-[color:var(--jarvis)] text-white text-xs font-medium inline-flex items-center gap-1.5 shrink-0"
            data-testid="add-subject-btn"
          >
            <Plus size={12} /> Nytt ämne
          </button>
        )}
      </div>

      {/* Edit/new form */}
      {editing && (
        <div className="card-soft p-4 mb-4" data-testid="subject-form">
          <p className="text-xs uppercase tracking-wider text-[color:var(--text-tertiary)] mb-3">
            {editing.isNew ? "Lägg till ämne" : `Redigera ${editing.name}`}
          </p>
          <div className="grid grid-cols-[80px_1fr] gap-2 items-center mb-3">
            <label className="text-xs text-[color:var(--text-secondary)]">Kod</label>
            <input
              type="text"
              value={editing.code || ""}
              onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })}
              placeholder="BL, MA, ENG..."
              maxLength={4}
              className="text-sm py-2 px-3 rounded border border-[color:var(--border-subtle)] bg-white"
              data-testid="subject-code-input"
            />
            <label className="text-xs text-[color:var(--text-secondary)]">Namn</label>
            <input
              type="text"
              value={editing.name || ""}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              placeholder="Bild, Matematik, Engelska..."
              className="text-sm py-2 px-3 rounded border border-[color:var(--border-subtle)] bg-white"
              data-testid="subject-name-input"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={cancelEdit}
              disabled={saving}
              className="px-3 py-2 rounded-full border border-[color:var(--border-subtle)] text-xs font-medium inline-flex items-center gap-1.5"
              data-testid="cancel-btn"
            >
              <X size={12} /> Avbryt
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="px-3 py-2 rounded-full bg-emerald-700 text-white text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-60"
              data-testid="save-btn"
            >
              <Save size={12} /> {saving ? "Sparar…" : "Spara"}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <p className="text-sm text-[color:var(--text-tertiary)]">Hämtar…</p>
      ) : (
        <div className="card-soft divide-y divide-[color:var(--border-subtle)]" data-testid="subjects-list">
          {items.length === 0 && (
            <p className="p-4 text-sm text-[color:var(--text-tertiary)] text-center">Inga ämnen ännu.</p>
          )}
          {items.map((s) => (
            <div
              key={s.id}
              className="flex items-center gap-3 px-3 py-2.5"
              data-testid={`subject-row-${s.code}`}
            >
              <span className="inline-flex items-center justify-center w-10 h-10 rounded bg-[color:var(--bg-elevated)] text-[11px] font-mono font-semibold text-[color:var(--text-secondary)] shrink-0">
                {s.code}
              </span>
              <p className="flex-1 text-sm">{s.name}</p>
              {isAdult && (
                <>
                  <button
                    onClick={() => startEdit(s)}
                    className="p-1.5 rounded hover:bg-[color:var(--bg-elevated)] text-[color:var(--text-tertiary)] hover:text-[color:var(--jarvis)]"
                    title="Redigera"
                    data-testid={`edit-${s.code}`}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => remove(s)}
                    className="p-1.5 rounded hover:bg-red-50 text-[color:var(--text-tertiary)] hover:text-red-600"
                    title="Ta bort"
                    data-testid={`remove-${s.code}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="card-soft p-3 mt-4 bg-[color:var(--bg-elevated)]/40">
        <p className="text-[11px] text-[color:var(--text-secondary)]">
          💡 <strong>Tips</strong>: Be Edit hjälpa! Säg t.ex. <em>"Lägg till Kemi som KE i ämneskatalogen"</em> eller <em>"Byt namn på Bild till Bildkonst"</em>.
        </p>
      </div>
    </div>
  );
}
